import { all, put, takeLatest, takeEvery, call } from 'redux-saga/effects';
import { AnnouncementActionTypes, ANNOUNCEMENT_CATEGORY } from 'types';
import {
  getAnnouncementsSuccess,
  getAnnouncementsFailure,
  getListAnnouncementsSuccess,
  getListAnnouncementsFailure,
  hideAnnouncementSuccess,
  hideAnnouncementFailure,
  unhideAnnouncementSuccess,
  unhideAnnouncementFailure,
  deleteAnnouncementSuccess,
  deleteAnnouncementFailure,
  updateAnnouncementSuccess,
  updateAnnouncementFailure,
  addAnnouncementSuccess,
  addAnnouncementFailure,
  hideAnnouncementBulkSuccess,
  hideAnnouncementBulkFailure
} from './actions';
import {
  getAnnouncements,
  getListAnnouncements,
  hideAnnouncement,
  unhideAnnouncement,
  deleteAnnouncement,
  updateAnnouncement,
  addAnnouncement
} from 'services/annoncement.service';
import { fetchOTPStatus } from 'redux/auth/action';
import { sortBy } from 'lodash';

function* handleGetAnnouncements(request: any) {
  try {
    const response = yield getAnnouncements(request.payload);
    if (response?.error) {
      return yield put(getAnnouncementsFailure(response.error.message));
    }

    if (response?.Config) {
      const isOTPActivated = response.Config?.isOTPActive
        ? response.Config.isOTPActive
        : false;
      yield put(fetchOTPStatus(isOTPActivated));
    }

    if (response?.Notices) {
      const sortedArr = sortBy(response.Notices, ['updatedAt']);
      const importantAnnouncements = sortedArr.filter(
        (n: any) => n.category === ANNOUNCEMENT_CATEGORY.IMPORTANT
      );
      const noticeAnnouncements = sortedArr.filter(
        (n: any) => n.category === ANNOUNCEMENT_CATEGORY.NOTICE
      );
      const infoAnnouncements = sortedArr.filter(
        (n: any) => n.category === ANNOUNCEMENT_CATEGORY.INFORMATION
      );
      yield put(
        getAnnouncementsSuccess([
          ...importantAnnouncements,
          ...noticeAnnouncements,
          ...infoAnnouncements
        ])
      );
    } else {
      yield put(getAnnouncementsFailure('Unable to get announcement'));
    }
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(getAnnouncementsFailure(errMsg));
  }
}

function* handleGetListAnnouncements(request: any) {
  try {
    const response = yield getListAnnouncements(request.payload);
    if (response?.error) {
      return yield put(getListAnnouncementsFailure(response.error.message));
    }

    if (response?.Notices) {
      yield put(getListAnnouncementsSuccess([response]));
    } else {
      yield put(getListAnnouncementsFailure('Unable to get announcement'));
    }
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(getListAnnouncementsFailure(errMsg));
  }
}

function* handleHideAnnouncement(request: any) {
  try {
    const response = yield hideAnnouncement(request.payload);
    if (response?.error) {
      return yield put(hideAnnouncementFailure(response.error.message));
    }

    yield put(hideAnnouncementSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(hideAnnouncementFailure(errMsg));
  }
}

function* handleUnhideAnnouncement(request: any) {
  try {
    const response = yield unhideAnnouncement(request.payload);
    if (response?.error) {
      return yield put(unhideAnnouncementFailure(response.error.message));
    }

    yield put(unhideAnnouncementSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(unhideAnnouncementFailure(errMsg));
  }
}

function* handleHideAnnouncementBulk(request: any) {
  try {
    const response = yield all(
      request.payload.map((payload: any) => call(hideAnnouncement, payload))
    );
    if (response?.error) {
      return yield put(hideAnnouncementBulkFailure(response.error.message));
    }

    yield put(hideAnnouncementBulkSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(hideAnnouncementBulkFailure(errMsg));
  }
}

function* handleDeleteAnnouncement(request: any) {
  try {
    const response = yield deleteAnnouncement(request.payload);
    if (response?.error) {
      return yield put(deleteAnnouncementFailure(response.error.message));
    }

    yield put(deleteAnnouncementSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(deleteAnnouncementFailure(errMsg));
  }
}

function* handleUpdateAnnouncement(request: any) {
  try {
    const response = yield updateAnnouncement(request.payload);
    if (response?.error) {
      return yield put(updateAnnouncementFailure(response.error.message));
    }

    yield put(updateAnnouncementSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(updateAnnouncementFailure(errMsg));
  }
}

function* handleAddAnnouncement(request: any) {
  try {
    const response = yield addAnnouncement(request.payload);
    if (response?.error) {
      return yield put(addAnnouncementFailure(response.error.message));
    }

    yield put(addAnnouncementSuccess());
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(addAnnouncementFailure(errMsg));
  }
}

function* watchGetAnnouncements() {
  yield takeLatest(
    AnnouncementActionTypes.GET_ANNOUNCEMENTS_START,
    handleGetAnnouncements
  );
}

function* watchGetListAnnouncements() {
  yield takeLatest(
    AnnouncementActionTypes.GET_LIST_ANNOUNCEMENTS_START,
    handleGetListAnnouncements
  );
}

function* watchHideAnnouncement() {
  // takeEvery so that hiding multiple announcements simultaneously doesn't cancel each other
  yield takeEvery(
    AnnouncementActionTypes.HIDE_ANNOUNCEMENT_START,
    handleHideAnnouncement
  );
}

function* watchUnhideAnnouncement() {
  yield takeEvery(
    AnnouncementActionTypes.UNHIDE_ANNOUNCEMENT_START,
    handleUnhideAnnouncement
  );
}

function* watchHideAnnouncementBulk() {
  yield takeLatest(
    AnnouncementActionTypes.HIDE_ANNOUNCEMENT_BULK_START,
    handleHideAnnouncementBulk
  );
}

function* watchDeleteAnnouncement() {
  yield takeLatest(
    AnnouncementActionTypes.DELETE_ANNOUNCEMENT_START,
    handleDeleteAnnouncement
  );
}

function* watchUpdateAnnouncement() {
  yield takeLatest(
    AnnouncementActionTypes.UPDATE_ANNOUNCEMENT_START,
    handleUpdateAnnouncement
  );
}

function* watchAddAnnouncement() {
  yield takeLatest(
    AnnouncementActionTypes.ADD_ANNOUNCEMENT_START,
    handleAddAnnouncement
  );
}

function* AnnouncementsSagas() {
  yield all([
    call(watchGetAnnouncements),
    call(watchHideAnnouncement),
    call(watchUnhideAnnouncement),
    call(watchGetListAnnouncements),
    call(watchDeleteAnnouncement),
    call(watchUpdateAnnouncement),
    call(watchAddAnnouncement),
    call(watchHideAnnouncementBulk)
  ]);
}

export default AnnouncementsSagas;
